import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { useProducts } from "../hooks/useProducts";

const SubscriptionPlans: React.FC = () => {
  const { user } = useAuth();
  const { loading, error, monthlyProduct, yearlyProduct, savingsPercentage } =
    useProducts();
  const [activePlan, setActivePlan] = useState<"monthly" | "yearly">("yearly");

  const getBuyNowUrl = (originalUrl: string) => {
    if (!user?.email) return originalUrl;

    const separator = originalUrl.includes("?") ? "&" : "?";
    return `${originalUrl}${separator}checkout[email]=${encodeURIComponent(
      user.email,
    )}&checkout[name]=${encodeURIComponent(
      user.displayName || "",
    )}&checkout[custom][firebase_uid]=${encodeURIComponent(user.uid)}`;
  };

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Toggle skeleton — yearly active */}
        <div className="flex justify-center">
          <div className="bg-white/5 rounded-full p-1 flex items-center">
            <div className="px-6 py-2 rounded-full">
              <div className="h-4 w-16 bg-slate-500/40 animate-pulse rounded"></div>
            </div>
            <div className="relative px-6 py-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600">
              <div className="h-4 w-14 bg-white/30 animate-pulse rounded"></div>
              {/* Save badge placeholder */}
              <div className="absolute -top-2 -right-2 h-4 w-14 bg-green-500/60 animate-pulse rounded-full"></div>
            </div>
          </div>
        </div>

        {/* Card skeleton */}
        <div className="max-w-lg mx-auto">
          <div style={{ height: "280px" }} className="relative">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 h-full relative overflow-hidden">
              {/* Logo circle */}
              <div className="absolute top-4 right-4 w-20 h-20 rounded-full bg-slate-600/40 animate-pulse"></div>

              <div className="relative z-10">
                {/* Price row */}
                <div className="flex items-baseline mb-3 mt-2">
                  <div className="h-12 w-32 bg-blue-500/30 animate-pulse rounded"></div>
                  <div className="h-5 w-24 bg-slate-500/20 animate-pulse rounded ml-2"></div>
                </div>

                {/* Strikethrough + savings badge */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-4 w-16 bg-slate-500/30 animate-pulse rounded"></div>
                  <div className="h-5 w-14 bg-green-500/20 animate-pulse rounded-full"></div>
                </div>

                {/* Green savings banner */}
                <div className="h-9 w-full bg-green-900/30 border border-green-500/20 animate-pulse rounded-lg mb-4"></div>

                {/* Description line */}
                <div className="h-4 w-4/5 bg-slate-500/20 animate-pulse rounded mb-3"></div>

                {/* Billed-as line */}
                <div className="h-4 w-40 bg-slate-500/20 animate-pulse rounded"></div>
              </div>
            </div>
          </div>

          {/* CTA button */}
          <div className="mt-24">
            <div className="h-14 w-full bg-blue-500/60 animate-pulse rounded-xl"></div>
            <div className="h-3 w-52 bg-slate-500/20 animate-pulse rounded mt-2 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white/5 border border-red-400/30 rounded-xl p-6 text-center">
        <i className="fas fa-exclamation-circle text-red-400 text-2xl mb-3"></i>
        <p className="text-red-200 text-lg">{error}</p>
      </div>
    );
  }

  const activeProduct =
    activePlan === "monthly" ? monthlyProduct : yearlyProduct;

  if (!activeProduct) {
    return (
      <div className="bg-white/5 border border-red-400/30 rounded-xl p-6 text-center">
        <p className="text-red-200 text-lg">No subscription plans available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Plan Toggle */}
      <div className="flex justify-center">
        <div className="bg-white/5 rounded-full p-1 flex items-center">
          <button
            onClick={() => setActivePlan("monthly")}
            className={`relative px-6 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
              activePlan === "monthly"
                ? "text-white"
                : "text-blue-200 hover:text-white"
            }`}
          >
            {activePlan === "monthly" && (
              <motion.div
                layoutId="activePlanIndicator"
                className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full -z-10"
                initial={false}
                transition={{ type: "spring", duration: 0.5 }}
              />
            )}
            Monthly
          </button>
          <button
            onClick={() => setActivePlan("yearly")}
            className={`relative px-6 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
              activePlan === "yearly"
                ? "text-white"
                : "text-blue-200 hover:text-white"
            }`}
          >
            {activePlan === "yearly" && (
              <motion.div
                layoutId="activePlanIndicator"
                className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full -z-10"
                initial={false}
                transition={{ type: "spring", duration: 0.5 }}
              />
            )}
            Yearly
            {savingsPercentage > 0 && (
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                Save {savingsPercentage}%
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Plan Details */}
      <div className="max-w-lg mx-auto">
        <div style={{ height: "280px" }} className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={activePlan}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 relative overflow-hidden h-full"
            >
              <div className="absolute -top-12 -right-12 w-24 h-24 bg-blue-500/20 rounded-full filter blur-xl"></div>
              <div className="absolute -bottom-12 -left-12 w-24 h-24 bg-indigo-500/20 rounded-full filter blur-xl"></div>

              <div className="absolute top-4 right-4 w-20 h-20 rounded-full bg-gradient-to-br from-blue-500/30 to-indigo-600/30 p-2 backdrop-blur-sm border border-white/10 flex items-center justify-center">
                <img
                  src="/a.png"
                  alt="Einstein AI Logo"
                  className="w-full h-full object-contain filter drop-shadow-lg"
                />
              </div>

              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-4"></h3>

                <div className="mb-4">
                  <div className="flex items-baseline">
                    <span className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
                      {activePlan === "monthly"
                        ? activeProduct.attributes.price_formatted.replace(
                            "/month",
                            "",
                          )
                        : `$${(
                            Math.floor(
                              (activeProduct.attributes.price / 100 / 12) * 100,
                            ) / 100
                          ).toFixed(2)}`}
                    </span>
                    <span className="text-blue-300/70 ml-2 text-lg">
                      / per month
                    </span>
                  </div>

                  {activePlan === "yearly" && monthlyProduct && (
                    <div className="mt-2 flex items-center">
                      <span className="text-blue-300/60 text-sm line-through mr-2">
                        {monthlyProduct.attributes.price_formatted.replace(
                          "/month",
                          "",
                        )}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                        {savingsPercentage}% off
                      </span>
                    </div>
                  )}
                </div>

                {activePlan === "monthly" && (
                  <div className="mt-6 mb-2 pl-2 border-l-2 border-blue-400/30">
                    <ul className="space-y-2 text-blue-100">
                      <li className="flex items-center text-sm">
                        <i className="fas fa-check text-blue-400 mr-2"></i>
                        Full access to AI learning content
                      </li>
                      <li className="flex items-center text-sm">
                        <i className="fas fa-check text-blue-400 mr-2"></i>
                        Unlimited topic exploration
                      </li>
                      <li className="flex items-center text-sm">
                        <i className="fas fa-check text-blue-400 mr-2"></i>
                        Priority customer support
                      </li>
                    </ul>
                  </div>
                )}

                {activePlan === "yearly" && (
                  <div className="mb-4 bg-green-900/30 border border-green-500/30 rounded-lg p-2 text-green-400 flex items-center">
                    <i className="fas fa-check-circle mr-2"></i>
                    <span>
                      <span className="font-bold">
                        {savingsPercentage}% savings
                      </span>{" "}
                      compared to monthly billing
                    </span>
                  </div>
                )}

                <div className="mb-2">
                  <div
                    className="text-blue-100 prose prose-sm prose-invert prose-headings:text-blue-200 prose-strong:text-white"
                    dangerouslySetInnerHTML={{
                      __html: activeProduct.attributes.description,
                    }}
                  />
                </div>

                {activePlan === "yearly" && (
                  <p className="text-blue-200 text-sm">
                    Billed as {activeProduct.attributes.price_formatted}
                  </p>
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="absolute bottom-[-100px] left-0 right-0">
            <a
              href={getBuyNowUrl(activeProduct.attributes.buy_now_url)}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium
                       shadow-lg hover:shadow-blue-500/20 transition-all duration-200
                       flex items-center justify-center space-x-2 text-lg"
            >
              <span>Start Learning Now</span>
              <i className="fas fa-arrow-right"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
